import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { usePage } from '../../hooks/usePage';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import { usePendingApprovals } from '../../contexts/PendingApprovalsContext';
import { isApprovalRequired } from '../../api/approvals';
import {
  payrollApi,
  PayrollBank,
  PayrollEmployee,
  PayrollEmployeeStatus,
  PayrollItemStatus,
  PayrollRun,
  PayrollRunDetail,
  PayrollRunStatus,
  PayrollTaxConfig,
  StatutoryBreakdown,
  LinkableAdmin,
  RemittanceReport,
  EmployeeHistory,
  PayrollSettings,
  CreateEmployeeDto,
} from '../../api/payroll';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Table } from '../../components/ui/Table';
import { Drawer } from '../../components/ui/Drawer';
import { Modal } from '../../components/ui/Modal';
import { Spinner } from '../../components/ui/Spinner';
import { Pagination } from '../../components/ui/Pagination';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';

type Tab = 'staff' | 'runs' | 'tax' | 'reports';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const formatNgn = (n: number) =>
  new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    maximumFractionDigits: 2,
  }).format(n);

const formatDate = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString('en-NG', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : '—';

const titleCase = (s: string) =>
  s.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

const RUN_STATUS_VARIANTS: Record<
  PayrollRunStatus,
  'success' | 'warning' | 'danger' | 'info' | 'neutral'
> = {
  draft: 'neutral',
  pending_approval: 'warning',
  processing: 'info',
  completed: 'success',
  partially_failed: 'warning',
  failed: 'danger',
  cancelled: 'neutral',
};

const ITEM_STATUS_VARIANTS: Record<
  PayrollItemStatus,
  'success' | 'warning' | 'danger' | 'info' | 'neutral'
> = {
  pending: 'neutral',
  processing: 'info',
  paid: 'success',
  failed: 'danger',
};

const selectStyle: React.CSSProperties = {
  padding: '10px 14px',
  fontSize: 14,
  fontFamily: 'var(--font-family)',
  color: 'var(--neutral-800)',
  background: 'var(--neutral-100)',
  border: '1px solid var(--neutral-300)',
  borderRadius: 'var(--radius-md)',
  outline: 'none',
  height: 40,
  width: '100%',
};

export const Payroll: React.FC = () => {
  usePage('Payroll');
  const { can } = useAuth();
  const canView = can('payroll.view');
  const canManage = can('payroll.manage');

  const [tab, setTab] = useState<Tab>('staff');

  if (!canView) {
    return (
      <Card padding={48}>
        <p style={{ textAlign: 'center', color: 'var(--neutral-500)', fontSize: 14 }}>
          You don't have permission to view payroll.
        </p>
      </Card>
    );
  }

  return (
    <>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {(['staff', 'runs', 'tax', 'reports'] as Tab[]).map((t) => {
          const active = tab === t;
          return (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              style={{
                padding: '8px 18px',
                borderRadius: 'var(--radius-md)',
                border: `1px solid ${active ? 'var(--blue-600)' : 'var(--neutral-300)'}`,
                background: active ? 'var(--blue-100)' : 'var(--neutral-100)',
                color: active ? 'var(--blue-700)' : 'var(--neutral-600)',
                fontSize: 14,
                fontWeight: active ? 600 : 500,
                cursor: 'pointer',
              }}
            >
              {t === 'staff'
                ? 'Staff'
                : t === 'runs'
                  ? 'Payroll runs'
                  : t === 'tax'
                    ? 'Tax & deductions'
                    : 'Reports'}
            </button>
          );
        })}
      </div>

      {tab === 'staff' ? (
        <StaffTab canManage={canManage} />
      ) : tab === 'runs' ? (
        <RunsTab canManage={canManage} />
      ) : tab === 'tax' ? (
        <TaxConfigTab canManage={canManage} />
      ) : (
        <ReportsTab />
      )}
    </>
  );
};

// ─── Staff tab ───────────────────────────────────────────────────────────────

const StaffTab: React.FC<{ canManage: boolean }> = ({ canManage }) => {
  const toast = useToast();
  const [employees, setEmployees] = useState<PayrollEmployee[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<PayrollEmployee | 'new' | null>(null);
  const [deleting, setDeleting] = useState<PayrollEmployee | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [importing, setImporting] = useState(false);

  const fetchEmployees = useCallback(async () => {
    try {
      setLoading(true);
      setEmployees(await payrollApi.listEmployees());
    } catch (err) {
      toast.error(err, 'Failed to load staff.');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void fetchEmployees();
  }, [fetchEmployees]);

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      setDeleteBusy(true);
      await payrollApi.deleteEmployee(deleting.id);
      toast.success(`${deleting.fullName} removed from payroll.`);
      setDeleting(null);
      await fetchEmployees();
    } catch (err) {
      toast.error(err, 'Failed to remove staff.');
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <>
      <Card padding={20}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 16,
            gap: 12,
          }}
        >
          <p style={{ fontSize: 13, color: 'var(--neutral-500)' }}>
            Staff paid through payroll. Only active staff are included in new runs.
          </p>
          {canManage ? (
            <div style={{ display: 'flex', gap: 8 }}>
              <Button size="sm" variant="secondary" onClick={() => setImporting(true)}>
                Import CSV
              </Button>
              <Button size="sm" onClick={() => setEditing('new')}>
                + Add staff
              </Button>
            </div>
          ) : null}
        </div>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
            <Spinner size={24} />
          </div>
        ) : (
          <Table<PayrollEmployee>
            data={employees ?? []}
            onRowClick={canManage ? (e) => setEditing(e) : undefined}
            emptyMessage="No staff yet. Add your first employee to get started."
            columns={[
              {
                key: 'name',
                header: 'Name',
                render: (e) => (
                  <div>
                    <div style={{ fontWeight: 600 }}>
                      {e.fullName}
                      {e.adminUserId ? (
                        <span
                          style={{
                            marginLeft: 6,
                            fontSize: 11,
                            color: 'var(--blue-700)',
                            fontWeight: 600,
                          }}
                        >
                          admin
                        </span>
                      ) : null}
                    </div>
                    {e.jobTitle || e.department ? (
                      <div style={{ fontSize: 12, color: 'var(--neutral-600)' }}>
                        {[e.jobTitle, e.department].filter(Boolean).join(' · ')}
                      </div>
                    ) : null}
                    {e.email ? (
                      <div style={{ fontSize: 12, color: 'var(--neutral-500)' }}>
                        {e.email}
                      </div>
                    ) : null}
                  </div>
                ),
              },
              {
                key: 'bank',
                header: 'Bank account',
                render: (e) => (
                  <div style={{ fontSize: 13 }}>
                    <div>{e.accountName}</div>
                    <div style={{ fontSize: 12, color: 'var(--neutral-500)', fontFamily: 'monospace' }}>
                      {e.accountNumber}
                    </div>
                  </div>
                ),
              },
              {
                key: 'salary',
                header: 'Monthly salary',
                render: (e) => (
                  <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                    {formatNgn(e.monthlySalary)}
                  </span>
                ),
              },
              {
                key: 'status',
                header: 'Status',
                render: (e) => (
                  <Badge variant={e.status === 'active' ? 'success' : 'neutral'}>
                    {titleCase(e.status)}
                  </Badge>
                ),
              },
              {
                key: 'actions',
                header: '',
                render: (e) =>
                  canManage ? (
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(ev) => {
                          ev.stopPropagation();
                          setDeleting(e);
                        }}
                      >
                        Remove
                      </Button>
                    </div>
                  ) : null,
              },
            ]}
          />
        )}
      </Card>

      {editing ? (
        <EmployeeDrawer
          employee={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await fetchEmployees();
          }}
        />
      ) : null}

      <ConfirmDialog
        isOpen={deleting !== null}
        title="Remove staff member"
        message={
          deleting
            ? `Remove ${deleting.fullName} from payroll? Past payment history is kept.`
            : ''
        }
        confirmLabel="Remove"
        variant="danger"
        loading={deleteBusy}
        onConfirm={() => void handleDelete()}
        onCancel={() => setDeleting(null)}
      />

      {importing ? (
        <ImportModal
          onClose={() => setImporting(false)}
          onImported={async () => {
            setImporting(false);
            await fetchEmployees();
          }}
        />
      ) : null}
    </>
  );
};

// ─── Employee create/edit drawer ─────────────────────────────────────────────

interface EmployeeForm {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  adminUserId: string;
  employeeNumber: string;
  jobTitle: string;
  department: string;
  startDate: string;
  endDate: string;
  bankCode: string;
  accountNumber: string;
  accountName: string;
  basic: string;
  housing: string;
  transport: string;
  other: string;
  /** Annual rent paid — the base for NTA 2025 rent relief. Blank means none
   *  declared, which means no relief; it can't be inferred from salary. */
  annualRent: string;
  status: PayrollEmployeeStatus;
}

const emptyForm: EmployeeForm = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  adminUserId: '',
  employeeNumber: '',
  jobTitle: '',
  department: '',
  startDate: '',
  endDate: '',
  bankCode: '',
  accountNumber: '',
  accountName: '',
  basic: '',
  housing: '',
  transport: '',
  other: '',
  annualRent: '',
  status: 'active',
};

const EmployeeDrawer: React.FC<{
  employee: PayrollEmployee | null;
  onClose: () => void;
  onSaved: () => void;
}> = ({ employee, onClose, onSaved }) => {
  const toast = useToast();
  const [form, setForm] = useState<EmployeeForm>(
    employee
      ? {
          firstName: employee.firstName,
          lastName: employee.lastName,
          email: employee.email ?? '',
          phone: employee.phone ?? '',
          adminUserId: employee.adminUserId ?? '',
          employeeNumber: employee.employeeNumber ?? '',
          jobTitle: employee.jobTitle ?? '',
          department: employee.department ?? '',
          startDate: employee.startDate ?? '',
          endDate: employee.endDate ?? '',
          bankCode: employee.bankCode,
          accountNumber: employee.accountNumber,
          accountName: employee.accountName,
          basic: String(employee.basic || ''),
          housing: String(employee.housing || ''),
          transport: String(employee.transport || ''),
          other: String(employee.other || ''),
          annualRent: String(employee.annualRent ?? ''),
          status: employee.status,
        }
      : emptyForm,
  );
  const [banks, setBanks] = useState<PayrollBank[]>([]);
  const [admins, setAdmins] = useState<LinkableAdmin[]>([]);
  const [taxConfig, setTaxConfig] = useState<PayrollTaxConfig | null>(null);
  const [splitInput, setSplitInput] = useState('');
  const [preview, setPreview] = useState<StatutoryBreakdown | null>(null);
  const [history, setHistory] = useState<EmployeeHistory | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    payrollApi.getBanks().then(setBanks).catch(() => setBanks([]));
    payrollApi.listLinkableAdmins().then(setAdmins).catch(() => setAdmins([]));
    payrollApi.getTaxConfig().then(setTaxConfig).catch(() => setTaxConfig(null));
    if (employee) {
      payrollApi
        .getEmployeeHistory(employee.id)
        .then(setHistory)
        .catch(() => setHistory(null));
    }
  }, [employee]);

  const components = {
    basic: Number(form.basic) || 0,
    housing: Number(form.housing) || 0,
    transport: Number(form.transport) || 0,
    other: Number(form.other) || 0,
  };
  const annualRent = Number(form.annualRent) || 0;
  const gross =
    components.basic + components.housing + components.transport + components.other;

  // Live net preview (debounced) whenever the components — or the rent the
  // relief is computed from — change.
  useEffect(() => {
    if (gross <= 0) {
      setPreview(null);
      return;
    }
    const timer = setTimeout(() => {
      payrollApi
        .previewNet({ ...components, annualRent })
        .then(setPreview)
        .catch(() => setPreview(null));
    }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.basic, form.housing, form.transport, form.other, form.annualRent]);

  const applySplit = () => {
    const g = Number(splitInput);
    if (!g || g <= 0 || !taxConfig) {
      toast.warning('Enter a gross amount to split.');
      return;
    }
    const round2 = (n: number) => Math.round(n * 100) / 100;
    const b = round2((taxConfig.defaultBasicPercent / 100) * g);
    const h = round2((taxConfig.defaultHousingPercent / 100) * g);
    const t = round2((taxConfig.defaultTransportPercent / 100) * g);
    const o = round2(g - b - h - t);
    setForm((f) => ({
      ...f,
      basic: String(b),
      housing: String(h),
      transport: String(t),
      other: String(o),
    }));
  };

  const set = <K extends keyof EmployeeForm>(key: K, value: EmployeeForm[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const onPickAdmin = (id: string) => {
    set('adminUserId', id);
    const admin = admins.find((a) => a.id === id);
    if (admin) {
      // Prefill identity from the admin account; salary/bank still required.
      setForm((f) => ({
        ...f,
        adminUserId: id,
        firstName: f.firstName || admin.firstName,
        lastName: f.lastName || admin.lastName,
        email: f.email || admin.email,
      }));
    }
  };

  const verify = async () => {
    if (!form.bankCode || form.accountNumber.length < 10) {
      toast.warning('Enter a bank and a valid account number first.');
      return;
    }
    try {
      setVerifying(true);
      const result = await payrollApi.verifyAccount(form.accountNumber, form.bankCode);
      set('accountName', result.accountName);
      toast.success(`Account resolved: ${result.accountName}`);
    } catch (err) {
      toast.error(err, 'Could not verify that account.');
    } finally {
      setVerifying(false);
    }
  };

  const save = async () => {
    if (!form.firstName.trim() || !form.lastName.trim()) {
      toast.warning('First and last name are required.');
      return;
    }
    if (!form.bankCode || !form.accountNumber || !form.accountName) {
      toast.warning('Verify the bank account before saving.');
      return;
    }
    if (gross <= 0) {
      toast.warning('Enter the salary components (or split a gross amount).');
      return;
    }
    const payload = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      email: form.email.trim() || undefined,
      phone: form.phone.trim() || undefined,
      adminUserId: form.adminUserId || null,
      employeeNumber: form.employeeNumber.trim() || null,
      jobTitle: form.jobTitle.trim() || null,
      department: form.department.trim() || null,
      startDate: form.startDate || null,
      endDate: form.endDate || null,
      bankCode: form.bankCode,
      accountNumber: form.accountNumber,
      accountName: form.accountName,
      basic: components.basic,
      housing: components.housing,
      transport: components.transport,
      other: components.other,
      annualRent: form.annualRent.trim() === '' ? null : annualRent,
      status: form.status,
    };
    try {
      setSaving(true);
      if (employee) {
        await payrollApi.updateEmployee(employee.id, payload);
        toast.success('Staff updated.');
      } else {
        await payrollApi.createEmployee(payload);
        toast.success('Staff added.');
      }
      onSaved();
    } catch (err) {
      toast.error(err, 'Failed to save staff.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      isOpen
      onClose={onClose}
      title={employee ? 'Edit staff' : 'Add staff'}
      subtitle={employee?.fullName}
      width={520}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--neutral-700)' }}>
          Link to an admin login (optional)
          <select
            value={form.adminUserId}
            onChange={(e) => onPickAdmin(e.target.value)}
            style={{ ...selectStyle, marginTop: 6 }}
          >
            <option value="">Not linked</option>
            {/* Keep the currently linked admin selectable even though the
                linkable list excludes already-linked admins. */}
            {employee?.adminUserId &&
            !admins.some((a) => a.id === employee.adminUserId) ? (
              <option value={employee.adminUserId}>Currently linked admin</option>
            ) : null}
            {admins.map((a) => (
              <option key={a.id} value={a.id}>
                {a.firstName} {a.lastName} · {a.email}
              </option>
            ))}
          </select>
        </label>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Input
            label="First name"
            value={form.firstName}
            onChange={(e) => set('firstName', e.target.value)}
          />
          <Input
            label="Last name"
            value={form.lastName}
            onChange={(e) => set('lastName', e.target.value)}
          />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Input
            label="Email (optional)"
            value={form.email}
            onChange={(e) => set('email', e.target.value)}
          />
          <Input
            label="Phone (optional)"
            value={form.phone}
            onChange={(e) => set('phone', e.target.value)}
          />
        </div>

        {/* HR / employment */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <Input
            label="Staff no."
            value={form.employeeNumber}
            onChange={(e) => set('employeeNumber', e.target.value)}
          />
          <Input
            label="Job title"
            value={form.jobTitle}
            onChange={(e) => set('jobTitle', e.target.value)}
          />
          <Input
            label="Department"
            value={form.department}
            onChange={(e) => set('department', e.target.value)}
          />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Input
            label="Start date"
            type="date"
            value={form.startDate}
            onChange={(e) => set('startDate', e.target.value)}
          />
          <Input
            label="End date"
            type="date"
            value={form.endDate}
            onChange={(e) => set('endDate', e.target.value)}
          />
        </div>

        <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--neutral-700)' }}>
          Bank
          <select
            value={form.bankCode}
            onChange={(e) => {
              set('bankCode', e.target.value);
              set('accountName', '');
            }}
            style={{ ...selectStyle, marginTop: 6 }}
          >
            <option value="">Select bank…</option>
            {banks.map((b) => (
              <option key={b.bankCode} value={b.bankCode}>
                {b.bankName}
              </option>
            ))}
          </select>
        </label>

        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <div style={{ flex: 1 }}>
            <Input
              label="Account number"
              value={form.accountNumber}
              inputMode="numeric"
              onChange={(e) => {
                set('accountNumber', e.target.value.replace(/\D/g, ''));
                set('accountName', '');
              }}
            />
          </div>
          <Button variant="secondary" onClick={verify} loading={verifying} disabled={saving}>
            Verify
          </Button>
        </div>

        {form.accountName ? (
          <div
            style={{
              padding: '8px 12px',
              background: 'var(--green-100)',
              color: 'var(--green-900)',
              borderRadius: 'var(--radius-md)',
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            {form.accountName}
          </div>
        ) : null}

        {/* Salary structure — components drive statutory bases (pension on
            basic+housing+transport, NHF on basic). */}
        <div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-end',
              marginBottom: 8,
            }}
          >
            <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--neutral-700)' }}>
              Salary breakdown (₦/month)
            </label>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input
                value={splitInput}
                inputMode="decimal"
                placeholder="Gross"
                onChange={(e) => setSplitInput(e.target.value.replace(/[^\d.]/g, ''))}
                style={{ ...selectStyle, height: 32, width: 110, fontSize: 13 }}
              />
              <Button size="sm" variant="secondary" onClick={applySplit}>
                Auto-split
              </Button>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {(['basic', 'housing', 'transport', 'other'] as const).map((k) => (
              <Input
                key={k}
                label={k.charAt(0).toUpperCase() + k.slice(1)}
                value={form[k]}
                inputMode="decimal"
                onChange={(e) => set(k, e.target.value.replace(/[^\d.]/g, ''))}
              />
            ))}
          </div>
        </div>

        {/* Rent relief (NTA 2025) is claimable only against rent actually
            paid, so it needs a declared figure — left blank, PAYE is computed
            with no relief at all. */}
        <Input
          label="Annual rent (₦) — for rent relief"
          value={form.annualRent}
          inputMode="decimal"
          placeholder="Leave blank if none declared"
          onChange={(e) => set('annualRent', e.target.value.replace(/[^\d.]/g, ''))}
        />
        {preview && preview.rentRelief > 0 ? (
          <div style={{ fontSize: 12, color: 'var(--neutral-500)', marginTop: -6 }}>
            Relief applied: {formatNgn(preview.rentRelief)}/year
          </div>
        ) : null}

        {/* Net preview */}
        <div
          style={{
            padding: 12,
            background: 'var(--neutral-200)',
            borderRadius: 'var(--radius-md)',
            fontSize: 13,
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600 }}>
            <span>Gross</span>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatNgn(gross)}</span>
          </div>
          {preview ? (
            <>
              {[
                ['PAYE', preview.paye],
                ['Pension', preview.pensionEmployee],
                ['NHF', preview.nhf],
              ].map(([label, val]) => (
                <div
                  key={label as string}
                  style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--neutral-500)', marginTop: 4 }}
                >
                  <span>− {label}</span>
                  <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {formatNgn(val as number)}
                  </span>
                </div>
              ))}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontWeight: 700,
                  marginTop: 8,
                  paddingTop: 8,
                  borderTop: '1px solid var(--neutral-300)',
                  color: 'var(--blue-700)',
                }}
              >
                <span>Net pay</span>
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {formatNgn(preview.net)}
                </span>
              </div>
            </>
          ) : (
            <div style={{ color: 'var(--neutral-400)', marginTop: 4 }}>
              Enter a salary to preview deductions.
            </div>
          )}
        </div>

        <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--neutral-700)' }}>
          Status
          <select
            value={form.status}
            onChange={(e) => set('status', e.target.value as PayrollEmployeeStatus)}
            style={{ ...selectStyle, marginTop: 6 }}
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </label>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={save} loading={saving}>
            {employee ? 'Save changes' : 'Add staff'}
          </Button>
        </div>

        {employee && history ? (
          <div style={{ marginTop: 12, paddingTop: 16, borderTop: '1px solid var(--neutral-200)' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                marginBottom: 10,
              }}
            >
              <h3 style={{ fontSize: 14, fontWeight: 600, color: 'var(--neutral-800)' }}>
                Pay history
              </h3>
              <span style={{ fontSize: 12, color: 'var(--neutral-500)' }}>
                {history.ytd.year} YTD: net {formatNgn(history.ytd.net)} · PAYE{' '}
                {formatNgn(history.ytd.paye)}
              </span>
            </div>
            {history.history.length === 0 ? (
              <p style={{ fontSize: 13, color: 'var(--neutral-400)' }}>
                No payments yet.
              </p>
            ) : (
              <Table<EmployeeHistory['history'][number] & { id: string }>
                data={history.history.map((h, i) => ({ ...h, id: String(i) }))}
                columns={[
                  { key: 'period', header: 'Period', render: (h) => h.runLabel },
                  {
                    key: 'net',
                    header: 'Net',
                    render: (h) => (
                      <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {formatNgn(h.netAmount)}
                      </span>
                    ),
                  },
                  {
                    key: 'status',
                    header: 'Status',
                    render: (h) => (
                      <Badge variant={ITEM_STATUS_VARIANTS[h.status]}>
                        {titleCase(h.status)}
                      </Badge>
                    ),
                  },
                ]}
              />
            )}
          </div>
        ) : null}
      </div>
    </Drawer>
  );
};

// ─── Runs tab ────────────────────────────────────────────────────────────────

const RunsTab: React.FC<{ canManage: boolean }> = ({ canManage }) => {
  const toast = useToast();
  const [runs, setRuns] = useState<PayrollRun[] | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [openRunId, setOpenRunId] = useState<string | null>(null);
  const limit = 25;

  const fetchRuns = useCallback(async () => {
    try {
      setLoading(true);
      const result = await payrollApi.listRuns({ page, limit });
      setRuns(result.items);
      setTotal(result.total);
    } catch (err) {
      toast.error(err, 'Failed to load payroll runs.');
    } finally {
      setLoading(false);
    }
  }, [page, toast]);

  useEffect(() => {
    void fetchRuns();
  }, [fetchRuns]);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <>
      <ScheduleSettings canManage={canManage} />
      <Card padding={20}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 16,
            gap: 12,
          }}
        >
          <p style={{ fontSize: 13, color: 'var(--neutral-500)' }}>
            Each run pays active staff for a period. Submitting a run sends it
            for a second admin's approval before any money moves.
          </p>
          {canManage ? (
            <Button size="sm" onClick={() => setCreating(true)}>
              + New run
            </Button>
          ) : null}
        </div>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
            <Spinner size={24} />
          </div>
        ) : (
          <>
            <Table<PayrollRun>
              data={runs ?? []}
              onRowClick={(r) => setOpenRunId(r.id)}
              emptyMessage="No payroll runs yet."
              columns={[
                {
                  key: 'label',
                  header: 'Period',
                  render: (r) => <span style={{ fontWeight: 600 }}>{r.label}</span>,
                },
                {
                  key: 'itemCount',
                  header: 'Staff',
                  render: (r) => r.itemCount,
                },
                {
                  key: 'total',
                  header: 'Total',
                  render: (r) => (
                    <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                      {formatNgn(r.totalAmount)}
                    </span>
                  ),
                },
                {
                  key: 'status',
                  header: 'Status',
                  render: (r) => (
                    <Badge variant={RUN_STATUS_VARIANTS[r.status]}>
                      {titleCase(r.status)}
                    </Badge>
                  ),
                },
                {
                  key: 'createdAt',
                  header: 'Created',
                  render: (r) => formatDate(r.createdAt),
                },
              ]}
            />
            <Pagination
              page={page}
              totalPages={totalPages}
              total={total}
              onPageChange={setPage}
            />
          </>
        )}
      </Card>

      {creating ? (
        <NewRunModal
          onClose={() => setCreating(false)}
          onCreated={async (run) => {
            setCreating(false);
            await fetchRuns();
            setOpenRunId(run.id);
          }}
        />
      ) : null}

      {openRunId ? (
        <RunDrawer
          runId={openRunId}
          canManage={canManage}
          onClose={() => setOpenRunId(null)}
          onChanged={fetchRuns}
        />
      ) : null}
    </>
  );
};

// ─── New run modal ───────────────────────────────────────────────────────────

const NewRunModal: React.FC<{
  onClose: () => void;
  onCreated: (run: PayrollRunDetail) => void;
}> = ({ onClose, onCreated }) => {
  const toast = useToast();
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const create = async () => {
    try {
      setSaving(true);
      const run = await payrollApi.createRun({
        periodMonth: month,
        periodYear: year,
        notes: notes.trim() || undefined,
      });
      toast.success(`Draft run created for ${run.label}.`);
      onCreated(run);
    } catch (err) {
      toast.error(err, 'Failed to create run.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="New payroll run">
      <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <p style={{ fontSize: 13, color: 'var(--neutral-500)', margin: 0 }}>
          This snapshots every <strong>active</strong> staff member at their
          current salary into a draft run you can review before submitting.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--neutral-700)' }}>
            Month
            <select
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              style={{ ...selectStyle, marginTop: 6 }}
            >
              {MONTHS.map((m, i) => (
                <option key={m} value={i + 1}>
                  {m}
                </option>
              ))}
            </select>
          </label>
          <Input
            label="Year"
            type="number"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
          />
        </div>
        <Input
          label="Notes (optional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={create} loading={saving}>
            Create draft
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// ─── Run detail drawer ───────────────────────────────────────────────────────

const RunDrawer: React.FC<{
  runId: string;
  canManage: boolean;
  onClose: () => void;
  onChanged: () => void | Promise<void>;
}> = ({ runId, canManage, onClose, onChanged }) => {
  const toast = useToast();
  const { refresh: refreshPending } = usePendingApprovals();
  const [run, setRun] = useState<PayrollRunDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState<
    'submit' | 'cancel' | 'retry' | null
  >(null);
  const [busy, setBusy] = useState(false);

  const fetchRun = useCallback(async () => {
    try {
      setLoading(true);
      setRun(await payrollApi.getRun(runId));
    } catch (err) {
      toast.error(err, 'Failed to load run.');
    } finally {
      setLoading(false);
    }
  }, [runId, toast]);

  useEffect(() => {
    void fetchRun();
  }, [fetchRun]);

  const isDraft = run?.status === 'draft';
  const isPending = run?.status === 'pending_approval';

  const submit = async () => {
    if (!run) return;
    try {
      setBusy(true);
      const result = await payrollApi.submitRun(run.id);
      if (isApprovalRequired(result)) {
        toast.success('Payroll run submitted for approval.', {
          to: '/approvals',
          label: 'View Approvals',
        });
        await refreshPending();
      } else {
        toast.success(
          `Run paid: ${result.paid} paid, ${result.failed} failed.`,
        );
      }
      setConfirming(null);
      await Promise.all([fetchRun(), onChanged()]);
    } catch (err) {
      toast.error(err, 'Failed to submit run.');
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    if (!run) return;
    try {
      setBusy(true);
      await payrollApi.cancelRun(run.id);
      toast.success('Run cancelled.');
      setConfirming(null);
      await Promise.all([fetchRun(), onChanged()]);
    } catch (err) {
      toast.error(err, 'Failed to cancel run.');
    } finally {
      setBusy(false);
    }
  };

  /** Run an ops action, then refresh the run + parent list. */
  const doAction = async (
    fn: () => Promise<unknown>,
    successMsg: string,
    failMsg: string,
  ) => {
    try {
      setBusy(true);
      await fn();
      toast.success(successMsg);
      setConfirming(null);
      await Promise.all([fetchRun(), onChanged()]);
    } catch (err) {
      toast.error(err, failMsg);
    } finally {
      setBusy(false);
    }
  };

  const retryFailed = () =>
    doAction(
      () => payrollApi.retryFailed(runId),
      'Failed payments re-attempted.',
      'Failed to retry payments.',
    );
  const markPaid = (itemId: string) =>
    doAction(
      () => payrollApi.markItemPaid(runId, itemId),
      'Marked as paid.',
      'Failed to mark paid.',
    );
  const markFailed = (itemId: string) =>
    doAction(
      () => payrollApi.markItemFailed(runId, itemId),
      'Marked as failed.',
      'Failed to mark failed.',
    );
  const resend = (itemId: string) =>
    doAction(
      () => payrollApi.resendPayslip(runId, itemId),
      'Payslip re-sent.',
      'Failed to resend payslip.',
    );
  const removeStaff = (itemId: string) =>
    doAction(
      () => payrollApi.removeRunItem(runId, itemId),
      'Removed from run.',
      'Failed to remove staff.',
    );

  // Active staff not already in a draft run — the "add staff" picker.
  const [allActive, setAllActive] = useState<PayrollEmployee[]>([]);
  const [addId, setAddId] = useState('');
  useEffect(() => {
    if (isDraft && canManage) {
      payrollApi
        .listEmployees({ status: 'active' })
        .then(setAllActive)
        .catch(() => setAllActive([]));
    }
  }, [isDraft, canManage]);
  const inRun = new Set((run?.items ?? []).map((i) => i.employeeId));
  const addable = allActive.filter((e) => !inRun.has(e.id));
  const addStaff = () => {
    if (!addId) return;
    void doAction(
      () => payrollApi.addRunItem(runId, addId),
      'Staff added to run.',
      'Failed to add staff.',
    ).then(() => setAddId(''));
  };

  const paidTotal = useMemo(
    () =>
      (run?.items ?? [])
        .filter((i) => i.status === 'paid')
        .reduce((sum, i) => sum + i.netAmount, 0),
    [run],
  );
  const failedCount = (run?.items ?? []).filter(
    (i) => i.status === 'failed',
  ).length;
  const canRetry =
    canManage && failedCount > 0 && !isDraft && !isPending;

  return (
    <>
      <Drawer
        isOpen
        onClose={onClose}
        title={run?.label ?? 'Payroll run'}
        subtitle={run ? titleCase(run.status) : ''}
        width={640}
      >
        {loading || !run ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
            <Spinner size={24} />
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
              <Badge variant={RUN_STATUS_VARIANTS[run.status]}>
                {titleCase(run.status)}
              </Badge>
              <Badge variant="neutral">{run.itemCount} staff</Badge>
              <Badge variant="neutral">Gross {formatNgn(run.totalAmount)}</Badge>
              <Badge variant="neutral">Deductions {formatNgn(run.totalDeductions)}</Badge>
              <Badge variant="info">Net {formatNgn(run.totalNet)}</Badge>
              {run.status !== 'draft' && run.status !== 'cancelled' ? (
                <Badge variant="success">{formatNgn(paidTotal)} paid</Badge>
              ) : null}
            </div>

            {isPending ? (
              <div
                style={{
                  padding: 12,
                  marginBottom: 16,
                  background: 'var(--warning-100)',
                  color: 'var(--warning-900)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 13,
                }}
              >
                Waiting for a second admin to approve payment.{' '}
                <Link to="/approvals" style={{ fontWeight: 700, color: 'inherit' }}>
                  View Approvals →
                </Link>
              </div>
            ) : null}

            {run.createdByEmail ? (
              <p style={{ fontSize: 12, color: 'var(--neutral-500)', marginBottom: 16 }}>
                Created by {run.createdByEmail} on {formatDate(run.createdAt)}
                {run.notes ? ` · ${run.notes}` : ''}
              </p>
            ) : null}

            <Table<PayrollRunDetail['items'][number]>
              data={run.items}
              emptyMessage="No staff in this run."
              columns={[
                {
                  key: 'name',
                  header: 'Staff',
                  render: (i) => (
                    <div>
                      <div style={{ fontWeight: 600 }}>
                        {i.employeeName}
                        {i.prorated ? (
                          <span
                            style={{
                              marginLeft: 6,
                              fontSize: 10,
                              fontWeight: 700,
                              color: 'var(--warning-900)',
                              background: 'var(--warning-100)',
                              padding: '1px 5px',
                              borderRadius: 'var(--radius-full)',
                            }}
                          >
                            PRORATED
                          </span>
                        ) : null}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--neutral-500)', fontFamily: 'monospace' }}>
                        {i.accountNumber}
                      </div>
                    </div>
                  ),
                },
                {
                  key: 'amount',
                  header: 'Gross → Net',
                  render: (i) => (
                    <div style={{ fontVariantNumeric: 'tabular-nums' }}>
                      <div style={{ fontWeight: 600 }}>{formatNgn(i.netAmount)}</div>
                      <div style={{ fontSize: 11, color: 'var(--neutral-500)' }}>
                        gross {formatNgn(i.amount)} · −{formatNgn(i.totalDeductions)}
                      </div>
                    </div>
                  ),
                },
                {
                  key: 'status',
                  header: 'Status',
                  render: (i) => (
                    <div>
                      <Badge variant={ITEM_STATUS_VARIANTS[i.status]}>
                        {titleCase(i.status)}
                      </Badge>
                      {i.failureReason ? (
                        <div style={{ fontSize: 11, color: 'var(--danger-600)', marginTop: 2 }}>
                          {i.failureReason}
                        </div>
                      ) : null}
                    </div>
                  ),
                },
                {
                  key: 'actions',
                  header: '',
                  render: (i) =>
                    canManage ? (
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                        {i.status === 'processing' ? (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={busy}
                              onClick={() => void markPaid(i.id)}
                            >
                              Mark paid
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={busy}
                              onClick={() => void markFailed(i.id)}
                            >
                              Mark failed
                            </Button>
                          </>
                        ) : null}
                        {i.status === 'paid' ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={busy}
                            onClick={() => void resend(i.id)}
                          >
                            {i.payslipSentAt ? 'Resend payslip' : 'Send payslip'}
                          </Button>
                        ) : null}
                        {isDraft ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={busy}
                            onClick={() => void removeStaff(i.id)}
                          >
                            Remove
                          </Button>
                        ) : null}
                      </div>
                    ) : null,
                },
              ]}
            />

            {isDraft && canManage ? (
              <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
                <select
                  value={addId}
                  onChange={(e) => setAddId(e.target.value)}
                  style={{ ...selectStyle, flex: 1 }}
                >
                  <option value="">Add a staff member…</option>
                  {addable.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.fullName}
                      {e.jobTitle ? ` · ${e.jobTitle}` : ''}
                    </option>
                  ))}
                </select>
                <Button
                  variant="secondary"
                  onClick={addStaff}
                  disabled={busy || !addId}
                >
                  Add
                </Button>
              </div>
            ) : null}

            {canManage && (isDraft || isPending) ? (
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
                <Button
                  variant="secondary"
                  onClick={() => setConfirming('cancel')}
                  disabled={busy}
                >
                  Cancel run
                </Button>
                {isDraft ? (
                  <Button onClick={() => setConfirming('submit')} disabled={busy}>
                    Submit for payment
                  </Button>
                ) : null}
              </div>
            ) : null}

            {canRetry ? (
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
                <Button
                  variant="secondary"
                  onClick={() => setConfirming('retry')}
                  disabled={busy}
                >
                  Retry {failedCount} failed payment{failedCount === 1 ? '' : 's'}
                </Button>
              </div>
            ) : null}
          </>
        )}
      </Drawer>

      <ConfirmDialog
        isOpen={confirming === 'submit'}
        title="Submit payroll for payment"
        message={
          run
            ? `Submit ${run.label} (${run.itemCount} staff, ${formatNgn(
                run.totalAmount,
              )})? A second admin must approve before any transfer is made.`
            : ''
        }
        confirmLabel="Submit for approval"
        loading={busy}
        onConfirm={() => void submit()}
        onCancel={() => setConfirming(null)}
      />

      <ConfirmDialog
        isOpen={confirming === 'cancel'}
        title="Cancel payroll run"
        message={run ? `Cancel ${run.label}? This can't be undone.` : ''}
        confirmLabel="Cancel run"
        variant="danger"
        loading={busy}
        onConfirm={() => void cancel()}
        onCancel={() => setConfirming(null)}
      />

      <ConfirmDialog
        isOpen={confirming === 'retry'}
        title="Retry failed payments"
        message={
          run
            ? `Re-attempt ${failedCount} failed payment${
                failedCount === 1 ? '' : 's'
              } in ${run.label}? This sends real bank transfers to those staff. No new approval is needed.`
            : ''
        }
        confirmLabel="Retry payments"
        variant="danger"
        loading={busy}
        onConfirm={() => void retryFailed()}
        onCancel={() => setConfirming(null)}
      />
    </>
  );
};

// ─── Tax & deductions tab ────────────────────────────────────────────────────

const NUM_FIELDS: { key: keyof PayrollTaxConfig; label: string; suffix?: string }[] = [
  { key: 'pensionEmployeePercent', label: 'Pension — employee', suffix: '%' },
  { key: 'pensionEmployerPercent', label: 'Pension — employer', suffix: '%' },
  { key: 'nhfPercent', label: 'NHF', suffix: '%' },
  { key: 'rentReliefPercent', label: 'Rent relief — % of annual rent', suffix: '%' },
  { key: 'rentReliefAnnualCap', label: 'Rent relief — annual cap', suffix: '₦' },
  { key: 'taxFreeAnnualThreshold', label: 'Tax-free annual threshold', suffix: '₦' },
  // Repealed by the Nigeria Tax Act 2025; the field survives so a
  // reinstatement needs no deploy, but it should read 0.
  { key: 'minimumTaxPercent', label: 'Minimum tax (repealed — leave at 0)', suffix: '%' },
  { key: 'defaultBasicPercent', label: 'Default split — basic', suffix: '%' },
  { key: 'defaultHousingPercent', label: 'Default split — housing', suffix: '%' },
  { key: 'defaultTransportPercent', label: 'Default split — transport', suffix: '%' },
  { key: 'defaultOtherPercent', label: 'Default split — other', suffix: '%' },
];

const TaxConfigTab: React.FC<{ canManage: boolean }> = ({ canManage }) => {
  const toast = useToast();
  const [cfg, setCfg] = useState<PayrollTaxConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchCfg = useCallback(async () => {
    try {
      setLoading(true);
      setCfg(await payrollApi.getTaxConfig());
    } catch (err) {
      toast.error(err, 'Failed to load tax config.');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void fetchCfg();
  }, [fetchCfg]);

  const setField = (key: keyof PayrollTaxConfig, value: unknown) =>
    setCfg((c) => (c ? { ...c, [key]: value } : c));

  const setBand = (i: number, key: 'upTo' | 'rate', value: string) =>
    setCfg((c) => {
      if (!c) return c;
      const bands = c.bands.map((b, idx) =>
        idx === i
          ? { ...b, [key]: value === '' ? (key === 'upTo' ? null : 0) : Number(value) }
          : b,
      );
      return { ...c, bands };
    });

  const addBand = () =>
    setCfg((c) => (c ? { ...c, bands: [...c.bands, { upTo: null, rate: 0 }] } : c));
  const removeBand = (i: number) =>
    setCfg((c) => (c ? { ...c, bands: c.bands.filter((_, idx) => idx !== i) } : c));

  const save = async () => {
    if (!cfg) return;
    try {
      setSaving(true);
      const { id, updatedByEmail, updatedAt, ...patch } = cfg;
      void id;
      void updatedByEmail;
      void updatedAt;
      const saved = await payrollApi.updateTaxConfig(patch);
      setCfg(saved);
      toast.success('Tax configuration saved.');
    } catch (err) {
      toast.error(err, 'Failed to save tax config.');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !cfg) {
    return (
      <Card padding={48}>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Spinner size={24} />
        </div>
      </Card>
    );
  }

  const disabled = !canManage;

  return (
    <Card padding={20}>
      <div
        style={{
          padding: '10px 14px',
          background: 'var(--warning-100)',
          color: 'var(--warning-900)',
          borderRadius: 'var(--radius-md)',
          fontSize: 13,
          marginBottom: 20,
        }}
      >
        ⚠️ These figures drive real PAYE, pension and NHF withholding. Verify them
        with your accountant and keep them current with the law — they aren't tax
        advice. Changes apply to <strong>new</strong> runs only; existing runs keep
        the figures they were created with.
      </div>

      <div style={{ display: 'flex', gap: 20, marginBottom: 20, flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14 }}>
          <input
            type="checkbox"
            checked={cfg.pensionEnabled}
            disabled={disabled}
            onChange={(e) => setField('pensionEnabled', e.target.checked)}
          />
          Pension enabled
        </label>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14 }}>
          <input
            type="checkbox"
            checked={cfg.nhfEnabled}
            disabled={disabled}
            onChange={(e) => setField('nhfEnabled', e.target.checked)}
          />
          NHF enabled
        </label>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
          gap: 12,
          marginBottom: 24,
        }}
      >
        {NUM_FIELDS.map((f) => (
          <Input
            key={f.key}
            label={`${f.label}${f.suffix ? ` (${f.suffix})` : ''}`}
            value={String(cfg[f.key] ?? '')}
            inputMode="decimal"
            disabled={disabled}
            onChange={(e) =>
              setField(f.key, Number(e.target.value.replace(/[^\d.]/g, '')) || 0)
            }
          />
        ))}
      </div>

      <h3 style={{ fontSize: 14, fontWeight: 600, color: 'var(--neutral-800)', marginBottom: 8 }}>
        PAYE bands (annual taxable income)
      </h3>
      <p style={{ fontSize: 12, color: 'var(--neutral-500)', marginBottom: 12 }}>
        Each band's ceiling is cumulative. Leave the last band's ceiling blank for
        the top (open-ended) rate.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
        {cfg.bands.map((b, i) => (
          <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <input
              value={b.upTo === null ? '' : String(b.upTo)}
              placeholder={i === cfg.bands.length - 1 ? 'Top (blank)' : 'Up to ₦'}
              inputMode="decimal"
              disabled={disabled}
              onChange={(e) => setBand(i, 'upTo', e.target.value.replace(/[^\d.]/g, ''))}
              style={{ ...selectStyle, height: 36, flex: 1 }}
            />
            <input
              value={String(b.rate)}
              inputMode="decimal"
              disabled={disabled}
              onChange={(e) => setBand(i, 'rate', e.target.value.replace(/[^\d.]/g, ''))}
              style={{ ...selectStyle, height: 36, width: 90 }}
            />
            <span style={{ fontSize: 13, color: 'var(--neutral-500)' }}>%</span>
            {canManage ? (
              <Button size="sm" variant="ghost" onClick={() => removeBand(i)}>
                ✕
              </Button>
            ) : null}
          </div>
        ))}
        {canManage ? (
          <Button size="sm" variant="secondary" onClick={addBand} style={{ alignSelf: 'flex-start' }}>
            + Add band
          </Button>
        ) : null}
      </div>

      {canManage ? (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={save} loading={saving}>
            Save configuration
          </Button>
        </div>
      ) : null}
    </Card>
  );
};

// ─── Reports tab ─────────────────────────────────────────────────────────────

const MONTH_OPTIONS = MONTHS.map((m, i) => ({ label: m, value: i + 1 }));

const ReportsTab: React.FC = () => {
  const toast = useToast();
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [report, setReport] = useState<RemittanceReport | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setReport(await payrollApi.getRemittance(year, month));
    } catch (err) {
      toast.error(err, 'Failed to load remittance report.');
    } finally {
      setLoading(false);
    }
  }, [year, month, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const downloadCsv = () => {
    if (!report) return;
    const header = [
      'Employee',
      'Gross',
      'PAYE',
      'Pension (employee)',
      'Pension (employer)',
      'NHF',
      'Net',
    ];
    const rows = report.lines.map((l) =>
      [
        l.employeeName,
        l.gross,
        l.paye,
        l.pensionEmployee,
        l.pensionEmployer,
        l.nhf,
        l.net,
      ]
        .map((v) => (typeof v === 'string' ? `"${v.replace(/"/g, '""')}"` : v))
        .join(','),
    );
    const totals = [
      'TOTAL',
      report.gross,
      report.paye,
      report.pensionEmployee,
      report.pensionEmployer,
      report.nhf,
      report.net,
    ].join(',');
    const csv = [header.join(','), ...rows, totals].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payiq-remittance-${report.year}-${String(report.month).padStart(2, '0')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const summary: { label: string; value: number; accent?: string }[] = report
    ? [
        { label: 'PAYE', value: report.paye, accent: 'var(--blue-700)' },
        { label: 'Pension (total)', value: report.pensionTotal },
        { label: 'NHF', value: report.nhf },
        { label: 'Gross', value: report.gross },
        { label: 'Net paid', value: report.net },
      ]
    : [];

  return (
    <Card padding={20}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          marginBottom: 20,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
            style={{ ...selectStyle, width: 150, height: 36 }}
          >
            {MONTH_OPTIONS.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
          <input
            type="number"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            style={{ ...selectStyle, width: 100, height: 36 }}
          />
        </div>
        <Button
          variant="secondary"
          onClick={downloadCsv}
          disabled={!report || report.headcount === 0}
        >
          Download CSV
        </Button>
      </div>

      <p style={{ fontSize: 13, color: 'var(--neutral-500)', marginBottom: 16 }}>
        Statutory amounts withheld on <strong>paid</strong> salaries for the
        period — what to remit to the tax authority, PFA and NHF.
      </p>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
          <Spinner size={24} />
        </div>
      ) : report && report.headcount > 0 ? (
        <>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
              gap: 12,
              marginBottom: 20,
            }}
          >
            {summary.map((s) => (
              <div
                key={s.label}
                style={{
                  padding: 14,
                  background: 'var(--neutral-200)',
                  borderRadius: 'var(--radius-md)',
                }}
              >
                <div style={{ fontSize: 11, color: 'var(--neutral-500)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  {s.label}
                </div>
                <div
                  style={{
                    fontSize: 18,
                    fontWeight: 700,
                    marginTop: 4,
                    color: s.accent ?? 'var(--neutral-800)',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {formatNgn(s.value)}
                </div>
              </div>
            ))}
          </div>
          <div style={{ fontSize: 13, color: 'var(--neutral-500)', marginBottom: 8 }}>
            {report.headcount} staff paid
          </div>
          <Table<RemittanceReport['lines'][number] & { id: string }>
            data={report.lines.map((l, i) => ({ ...l, id: String(i) }))}
            emptyMessage="No paid salaries this period."
            columns={[
              { key: 'name', header: 'Employee', render: (l) => l.employeeName },
              { key: 'gross', header: 'Gross', render: (l) => formatNgn(l.gross) },
              { key: 'paye', header: 'PAYE', render: (l) => formatNgn(l.paye) },
              {
                key: 'pension',
                header: 'Pension (e/er)',
                render: (l) =>
                  `${formatNgn(l.pensionEmployee)} / ${formatNgn(l.pensionEmployer)}`,
              },
              { key: 'nhf', header: 'NHF', render: (l) => formatNgn(l.nhf) },
              { key: 'net', header: 'Net', render: (l) => formatNgn(l.net) },
            ]}
          />
        </>
      ) : (
        <p style={{ textAlign: 'center', color: 'var(--neutral-400)', padding: 32 }}>
          No paid salaries for {MONTHS[month - 1]} {year}.
        </p>
      )}
    </Card>
  );
};

// ─── Scheduled auto-draft settings ───────────────────────────────────────────

const ScheduleSettings: React.FC<{ canManage: boolean }> = ({ canManage }) => {
  const toast = useToast();
  const [settings, setSettings] = useState<PayrollSettings | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    payrollApi.getSettings().then(setSettings).catch(() => setSettings(null));
  }, []);

  const save = async (patch: Partial<PayrollSettings>) => {
    try {
      setSaving(true);
      const next = await payrollApi.updateSettings(patch);
      setSettings(next);
      toast.success('Schedule updated.');
    } catch (err) {
      toast.error(err, 'Failed to update schedule.');
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return null;

  return (
    <Card padding={16} style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14, fontWeight: 600 }}>
          <input
            type="checkbox"
            checked={settings.autoRunEnabled}
            disabled={!canManage || saving}
            onChange={(e) => void save({ autoRunEnabled: e.target.checked })}
          />
          Auto-create a draft run each month
        </label>
        <label style={{ fontSize: 13, color: 'var(--neutral-600)', display: 'flex', gap: 6, alignItems: 'center' }}>
          on day
          <input
            type="number"
            min={1}
            max={28}
            value={settings.autoRunDayOfMonth}
            disabled={!canManage || saving || !settings.autoRunEnabled}
            onChange={(e) =>
              void save({
                autoRunDayOfMonth: Math.min(28, Math.max(1, Number(e.target.value) || 1)),
              })
            }
            style={{ ...selectStyle, width: 64, height: 32 }}
          />
        </label>
        <span style={{ fontSize: 12, color: 'var(--neutral-500)' }}>
          A draft is created automatically; a maker still submits it for approval.
        </span>
      </div>
    </Card>
  );
};

// ─── CSV import ──────────────────────────────────────────────────────────────

/** Minimal CSV parser: header row to objects. Handles quoted fields + commas. */
function parseCsv(text: string): Record<string, string>[] {
  const rows: string[][] = [];
  let field = '';
  let row: string[] = [];
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else inQuotes = false;
      } else field += c;
    } else if (c === '"') inQuotes = true;
    else if (c === ',') {
      row.push(field);
      field = '';
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && text[i + 1] === '\n') i++;
      row.push(field);
      field = '';
      if (row.some((v) => v.trim() !== '')) rows.push(row);
      row = [];
    } else field += c;
  }
  if (field !== '' || row.length) {
    row.push(field);
    if (row.some((v) => v.trim() !== '')) rows.push(row);
  }
  if (rows.length < 2) return [];
  const header = rows[0].map((h) => h.trim());
  return rows.slice(1).map((r) => {
    const obj: Record<string, string> = {};
    header.forEach((h, i) => (obj[h] = (r[i] ?? '').trim()));
    return obj;
  });
}

const NUMERIC_FIELDS = [
  'monthlySalary',
  'basic',
  'housing',
  'transport',
  'other',
  'annualRent',
];

/** Map a parsed CSV row (camelCase headers) to a CreateEmployeeDto. */
function rowToEmployee(r: Record<string, string>): CreateEmployeeDto {
  const dto: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(r)) {
    if (v === '') continue;
    dto[k] = NUMERIC_FIELDS.includes(k) ? Number(v) : v;
  }
  return dto as unknown as CreateEmployeeDto;
}

const ImportModal: React.FC<{
  onClose: () => void;
  onImported: () => void;
}> = ({ onClose, onImported }) => {
  const toast = useToast();
  const [rows, setRows] = useState<CreateEmployeeDto[]>([]);
  const [fileName, setFileName] = useState('');
  const [busy, setBusy] = useState(false);

  const onFile = async (file: File) => {
    try {
      const text = await file.text();
      const parsed = parseCsv(text).map(rowToEmployee);
      if (parsed.length === 0) {
        toast.warning('No rows found. Include a header row and at least one record.');
        return;
      }
      setRows(parsed);
      setFileName(file.name);
    } catch {
      toast.error(null, 'Could not read that file.');
    }
  };

  const submit = async () => {
    if (rows.length === 0) return;
    try {
      setBusy(true);
      const result = await payrollApi.importEmployees(rows);
      if (result.errors.length === 0) {
        toast.success(`Imported ${result.created} staff.`);
        onImported();
      } else {
        toast.warning(
          `Imported ${result.created}; ${result.errors.length} row(s) failed: ` +
            result.errors
              .slice(0, 3)
              .map((e) => `row ${e.row}: ${e.message}`)
              .join('; '),
        );
        if (result.created > 0) onImported();
      }
    } catch (err) {
      toast.error(err, 'Import failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Import staff from CSV">
      <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <p style={{ fontSize: 13, color: 'var(--neutral-500)', margin: 0 }}>
          CSV with a header row. Required columns: firstName, lastName, bankCode,
          accountNumber, accountName, monthlySalary. Optional: email, phone,
          employeeNumber, jobTitle, department, startDate, endDate, basic, housing,
          transport, other, annualRent.
        </p>
        <input
          type="file"
          accept=".csv,text/csv"
          onChange={(e) => e.target.files?.[0] && void onFile(e.target.files[0])}
          style={{ fontSize: 13 }}
        />
        {rows.length > 0 ? (
          <div style={{ fontSize: 13, color: 'var(--neutral-700)' }}>
            <strong>{rows.length}</strong> row{rows.length === 1 ? '' : 's'} parsed from {fileName}.
          </div>
        ) : null}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={submit} loading={busy} disabled={rows.length === 0}>
            Import {rows.length || ''}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
